import { HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';

import { SKIP_ERROR_TOAST } from '../interceptors/auth.interceptor';
import { ApiService } from './api.service';
import { CurrentUserService } from './current-user.service';
import { StoreContextService } from './store-context.service';

/** `Status` value meaning "Active" on `StorePrintFormatResponse` (`API.Domain.Features.Common.Enums.Status`). */
const ACTIVE_STATUS = 2;

/**
 * Gates every store on having a default (Active) print format configured —
 * required before staff can print item labels.
 *
 * Watches {@link StoreContextService}'s active store and, once it resolves to
 * `'valid'`, calls `GET StorePrintFormats/Default` for that store. A 404
 * means the store has no Active print format yet, so `requiredStoreId` is set
 * and {@link RequiredPrintFormatModalComponent} (rendered once at the app
 * root) shows a mandatory, non-dismissible prompt until one is configured.
 * Re-runs automatically whenever the active store changes (including the
 * topbar store switch), since both are driven by the same `StoreContextService` signals.
 */
@Injectable({ providedIn: 'root' })
export class DefaultPrintFormatRequirementService {
  private readonly api = inject(ApiService);
  private readonly storeCtx = inject(StoreContextService);
  private readonly currentUser = inject(CurrentUserService);

  private readonly _requiredStoreId = signal<number | null>(null);
  readonly requiredStoreId = this._requiredStoreId.asReadonly();

  private readonly _submitting = signal(false);
  readonly submitting = this._submitting.asReadonly();

  /** Avoids re-checking the same store repeatedly on unrelated signal changes. */
  private lastCheckedStoreId: number | null = null;

  constructor() {
    effect(() => {
      const status = this.storeCtx.storeStatus();
      const storeId = this.storeCtx.selectedStoreId();
      if (status === 'valid' && storeId != null) {
        this.checkStore(storeId);
      }
    });
  }

  private checkStore(storeId: number): void {
    if (this.lastCheckedStoreId === storeId) return;
    this.lastCheckedStoreId = storeId;

    const params = new HttpParams().set('StoreId', storeId);
    // A 404 here just means "no default configured yet" — expected, not an error to surface.
    const context = new HttpContext().set(SKIP_ERROR_TOAST, true);
    this.api.get('StorePrintFormats/Default', params, context).subscribe({
      next: () => this._requiredStoreId.set(null),
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this._requiredStoreId.set(storeId);
        }
        // Any other error (network blip, auth hiccup) — don't force the
        // mandatory prompt off the back of an unrelated failure.
      }
    });
  }

  /** Creates the pending store's print format directly as Active — the API deactivates any prior default itself. */
  configureDefault(printFormatId: number, onDone?: () => void, onError?: () => void): void {
    const storeId = this._requiredStoreId();
    if (storeId == null) return;

    this._submitting.set(true);
    const companyId = Number(this.currentUser.companyId()) || 0;

    this.api.post('StorePrintFormats', { storeId, printFormatId, companyId, status: ACTIVE_STATUS }).subscribe({
      next: () => {
        this._submitting.set(false);
        this.lastCheckedStoreId = storeId;
        this._requiredStoreId.set(null);
        onDone?.();
      },
      error: () => {
        this._submitting.set(false);
        onError?.();
      }
    });
  }
}
